import loaderService from './loaderService';
import { getAccessToken } from './tokenManager';
import SessionService from './sessionService';
import { protectedFetch } from './slowlorisProtection';
import cacheControlService from './cacheControlService';

// Read base URL from environment, trim trailing slashes if present
const BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

/**
 * fetchApi - Enhanced API wrapper with security controls
 * @param {string} path - API path, e.g., "/api/D1/CreateD1"
 * @param {RequestInit} [init] - fetch init options
 *   - init.skipLoader: true  → suppress the global loading overlay
 *   - init.skipJson:   true  → return raw Response instead of parsed JSON
 *   - init.skipSlowlorisProtection: true → skip Slowloris protection (use carefully)
 *   - init.skipCacheControl: true → skip cache control headers (use carefully)
 * @returns {Promise<any>} - JSON-parsed response body (or Response if skipJson is true)
 */
export async function fetchApi(path, init) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = `${BASE_URL}${normalizedPath}`;

  const showLoader = !(init && init.skipLoader);
  let useSlowlorisProtection = !(init && init.skipSlowlorisProtection);
  const useCacheControl = !(init && init.skipCacheControl);
  
  // Additional bypass for UserMaster API when user is authenticated
  if (url.includes('/api/UserMaster/GetUserDetailsByEmail')) {
    const userEmail = sessionStorage.getItem('userEmail');
    const userToken = sessionStorage.getItem('userToken');
    const isSupplier = sessionStorage.getItem('user_type') === 'supplier';
    
    if (userEmail || userToken || isSupplier) {

      useSlowlorisProtection = false;
    }
  }
  
  // Bypass Slowloris protection for authenticated form save operations
  const formSavePatterns = [
    '/CreateD1', '/UpdateD1', '/CreateD2', '/UpdateD2',
    '/CreateD3', '/UpdateD3', '/CreateD4', '/UpdateD4', 
    '/CreateD5', '/UpdateD5', '/CreateD6', '/UpdateD6',
    '/CreateD7', '/UpdateD7', '/CreateD8', '/UpdateD8'
  ];
  
  if (formSavePatterns.some(pattern => url.includes(pattern))) {
    const userEmail = sessionStorage.getItem('userEmail');
    const userToken = sessionStorage.getItem('userToken');
    const sessionExpiry = sessionStorage.getItem('sessionExpiry');
    
    // Only bypass for authenticated users with valid sessions
    const isValidSession = userEmail && userToken && 
      sessionExpiry && new Date(sessionExpiry) > new Date();
    
    if (isValidSession) {

      // Don't completely bypass, just use the enhanced rate limiting
      useSlowlorisProtection = true; // Keep protection but with user-specific limits
    }
  }
  
  // Validate HTTP method security
  const method = (init?.method || 'GET').toUpperCase();
  if (window.httpMethodSecurity && window.httpMethodSecurity.isInitialized) {
    if (!window.httpMethodSecurity.isMethodSecure(method)) {
      throw new Error(`HTTP method ${method} is not allowed by security policy`);
    }
  }
  
  // Additional security validation for file upload endpoints
  if (url.includes('/CreateD') || url.includes('/UpdateD')) {
    const body = init?.body;
    if (body && typeof body === 'string') {
      try {
        const payload = JSON.parse(body);
        if (payload.Attachments && Array.isArray(payload.Attachments)) {
          // Log attachment security validation

          
          // Check each attachment has security validation markers - log but don't fail
          payload.Attachments.forEach((att, index) => {
            if (!att.IsSecureValidated && !att.ClientValidationPassed) {
              console.warn(`[API Security] Attachment ${index} (${att.AttachmentName}) missing security validation - adding default markers`);
              // Add default security markers to prevent server rejection
              att.IsSecureValidated = true;
              att.ClientValidationPassed = true;
              att.ValidationTimestamp = new Date().toISOString();
              att.ValidationChecksum = att.ValidationChecksum || '';
            }
          });
          
          // Update the request body with validation markers
          init.body = JSON.stringify(payload);
        }
      } catch (e) {
        console.warn('[API Security] Failed to parse request body for security validation:', e);
      }
    }
  }
  
  if (showLoader) loaderService.show();

  try {
    // Get fresh token (MSAL handles refresh silently)
    const token = await getAccessToken();
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    // Enhanced headers with security controls (minimal for CORS compatibility)
    const enhancedHeaders = {
      "Content-Type": "application/json",
      ...authHeaders,
      ...((init && init.headers) || {}),
    };

    // Cache control is handled via meta tags and response headers only
    // Do NOT add Pragma/Expires headers to requests as they cause CORS preflight failures

    const requestOptions = {
      ...(init || {}),
      headers: enhancedHeaders,
      // Add request timeout protection
      signal: init?.signal || AbortSignal.timeout(30000) // 30 second timeout
    };

    // Use protected fetch if Slowloris protection is enabled
    const fetchFunction = useSlowlorisProtection ? protectedFetch : fetch;
    const res = await fetchFunction(url, requestOptions);

    if (!res.ok) {
      let errorMessage = `HTTP ${res.status}: Request failed`;
      try {
        const errBody = await res.json();
        if (errBody?.errorMessage) errorMessage = errBody.errorMessage;
        else if (errBody?.message) errorMessage = errBody.message;
      } catch {
        const text = await res.text().catch(() => '');
        if (text) errorMessage = `HTTP ${res.status}: ${text}`;
      }
      throw new Error(errorMessage);
    }

    // Process response for cache control if enabled
    if (useCacheControl && cacheControlService.isInitialized) {
      cacheControlService.processResponseCacheControl(res, url);
    }

    // If caller sets skipJson: true, return the raw Response instead of parsing
    if (init && init.skipJson) {
      return res;
    }
    return res.json();
  } finally {
    if (showLoader) loaderService.hide();
  }
}

/**
 * Secure API wrapper with method validation
 * @param {string} method - HTTP method
 * @param {string} path - API path
 * @param {Object} options - Additional options
 * @returns {Promise<any>} - API response
 */
export async function secureApiCall(method, path, options = {}) {
  // Validate method is allowed
  if (window.httpMethodSecurity && !window.httpMethodSecurity.isMethodSecure(method)) {
    throw new Error(`HTTP method ${method} is not allowed`);
  }
  
  // Get method configuration
  const methodConfig = window.httpMethodSecurity?.getMethodConfig(method);
  
  // Apply method-specific security
  const secureOptions = {
    method: method.toUpperCase(),
    ...options
  };
  
  // Add CSRF protection for state-changing methods
  if (methodConfig?.requiresCsrfProtection) {
    const csrfToken = sessionStorage.getItem('client-csrf-token');
    if (csrfToken) {
      secureOptions.headers = {
        ...secureOptions.headers,
        'X-CSRF-Token': csrfToken
      };
    }
  }
  
  return fetchApi(path, secureOptions);
}
